import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { normalizeObjectStrings } from '../lib/utils';
import catalogFallbackImage from '../assets/catalog-fallback.svg';
import SupportWidget from '../components/SupportWidget';
import { 
  Heart, ArrowLeft, Plus, Gift, Edit2, Trash2, Check,
  DollarSign, ShoppingBag, Coffee, Search, ChefHat, Tv,
  Bed, UtensilsCrossed, Sofa, TreePine, Upload, X, Image
} from 'lucide-react';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ACCEPTED_IMAGE_LABEL = 'JPG, PNG, WEBP ou GIF';
const MAX_UPLOAD_MB = 12;
const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2
});
const FALLBACK_CATALOG_IMAGE = catalogFallbackImage;

const formatCurrency = (value) => BRL_FORMATTER.format(Number(value) || 0);

const applyFallbackImage = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = FALLBACK_CATALOG_IMAGE;
};

const extractApiErrorMessage = (error, fallback = 'Erro ao enviar imagem') => {
  const payload = error?.response?.data;
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }
  if (payload?.detail) {
    if (typeof payload.detail === 'string') {
      return payload.detail;
    }
    if (Array.isArray(payload.detail) && payload.detail.length > 0) {
      const first = payload.detail[0];
      if (typeof first?.msg === 'string') {
        return first.msg;
      }
    }
  }
  return fallback;
};

const validateUploadFile = (file) => {
  const fileType = (file?.type || '').toLowerCase();
  if (!ACCEPTED_IMAGE_TYPES.includes(fileType)) {
    toast.error(`Formato inválido. Use ${ACCEPTED_IMAGE_LABEL}.`);
    return false;
  }
  if ((file?.size || 0) > MAX_UPLOAD_MB * 1024 * 1024) {
    toast.error(`Imagem muito grande. Limite de ${MAX_UPLOAD_MB}MB.`);
    return false;
  }
  return true;
};

const CATEGORY_ICONS = {
  cozinha_mesa: <ChefHat className="w-5 h-5" />,
  eletroportateis: <Tv className="w-5 h-5" />,
  cama_banho_organizacao: <Bed className="w-5 h-5" />,
  casa_decoracao: <Sofa className="w-5 h-5" />,
  experiencias: <DollarSign className="w-5 h-5" />,
  cozinha: <ChefHat className="w-5 h-5" />,
  eletrodomesticos: <Tv className="w-5 h-5" />,
  cama_mesa_banho: <Bed className="w-5 h-5" />,
  sala_jantar: <UtensilsCrossed className="w-5 h-5" />,
  moveis: <Sofa className="w-5 h-5" />,
  area_externa: <TreePine className="w-5 h-5" />
};

export default function GiftManager() {
  const [loading, setLoading] = useState(true);
  const [gifts, setGifts] = useState([]);
  const [catalog, setCatalog] = useState({ categories: {}, items: [] });
  const [showModal, setShowModal] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [editingGift, setEditingGift] = useState(null);
  const [activeTab, setActiveTab] = useState('meus_presentes');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    category: 'casamento'
  });

  const [catalogItem, setCatalogItem] = useState(null);
  const [customPrice, setCustomPrice] = useState('');
  const [customName, setCustomName] = useState('');
  const [customImage, setCustomImage] = useState('');
  const [uploadingCatalog, setUploadingCatalog] = useState(false);
  const catalogFileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [giftsRes, catalogRes] = await Promise.all([
        axios.get(`${API}/gifts`, { withCredentials: true }),
        axios.get(`${API}/gifts/catalog`, { withCredentials: true })
      ]);
      setGifts(normalizeObjectStrings(giftsRes.data || []));
      setCatalog(normalizeObjectStrings(catalogRes.data || { categories: {}, items: [] }));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

    const handleImageUpload = async (file) => {
    if (!file || !validateUploadFile(file)) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = `${API}/images/${response.data.file_id}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Imagem enviada!');
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error('Sua sessão expirou. Faça login novamente.');
        navigate('/login', { replace: true });
        return;
      }
      toast.error(extractApiErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };
  const openModal = (gift = null, category = 'casamento') => {
    if (gift) {
      setEditingGift(gift);
      setFormData({
        name: gift.name,
        description: gift.description || '',
        price: gift.price.toString(),
        image_url: gift.image_url || '',
        category: gift.category
      });
    } else {
      setEditingGift(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        image_url: '',
        category: category
      });
    }
    setShowModal(true);
  };

  const openCatalogModal = (item) => {
    setCatalogItem(item);
    setCustomPrice(item.suggested_price.toString());
    setCustomName(item.name); // Default to original name
    setCustomImage(item.image_url || ''); // Default to catalog image
    setShowCatalogModal(true);
  };

    const handleCatalogImageUpload = async (file) => {
    if (!file || !validateUploadFile(file)) return;

    setUploadingCatalog(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = `${API}/images/${response.data.file_id}`;
      setCustomImage(imageUrl);
      toast.success('Imagem personalizada enviada!');
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error('Sua sessão expirou. Faça login novamente.');
        navigate('/login', { replace: true });
        return;
      }
      toast.error(extractApiErrorMessage(error));
    } finally {
      setUploadingCatalog(false);
    }
  };
  const handleGiftFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    handleImageUpload(selectedFile);
  };

  const handleCatalogFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    handleCatalogImageUpload(selectedFile);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingGift) {
        await axios.put(`${API}/gifts/${editingGift.gift_id}`, payload, { withCredentials: true });
        toast.success('Presente atualizado!');
      } else {
        await axios.post(`${API}/gifts`, payload, { withCredentials: true });
        toast.success('Presente adicionado!');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar presente');
    }
  };

  const handleAddFromCatalog = async () => {
    if (!catalogItem) return;
    
    try {
      // Build query params with custom values
      const params = new URLSearchParams();
      params.append('catalog_id', catalogItem.catalog_id);
      params.append('custom_price', parseFloat(customPrice));
      if (customName && customName !== catalogItem.name) {
        params.append('custom_name', customName);
      }
      if (customImage && customImage !== catalogItem.image_url) {
        params.append('custom_image', customImage);
      }
      
      await axios.post(`${API}/gifts/from-catalog?${params.toString()}`, {}, { withCredentials: true });
      toast.success(`${customName || catalogItem.name} adicionado à sua lista!`);
      setShowCatalogModal(false);
      setCatalogItem(null);
      setCustomName('');
      setCustomImage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar presente');
    }
  };

  const handleDelete = async (giftId) => {
    if (!window.confirm('Tem certeza que deseja excluir este presente?')) return;
    try {
      await axios.delete(`${API}/gifts/${giftId}`, { withCredentials: true });
      toast.success('Presente excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir presente');
    }
  };

  // Filter catalog items
  const filteredCatalog = catalog.items.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const getCatalogCategoryLabel = (categoryKey) => catalog.categories?.[categoryKey] || 'Categoria';

  const casamentoGifts = gifts.filter(g => g.category === 'casamento');
  const chaGifts = gifts.filter(g => g.category === 'cha_de_panela');
  const availableGifts = gifts.filter(g => g.is_available);
  const purchasedGifts = gifts.filter(g => !g.is_available);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#E8B7B1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#333333] font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user?.has_subscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF] p-6">
        <div className="text-center max-w-md">
          <Gift className="w-16 h-16 text-[#C8A97E] mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-[#1C1C1C] mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Ative sua conta primeiro
          </h1>
          <p className="text-[#333333] mb-6">
            Para gerenciar sua lista de presentes, você precisa ativar sua assinatura.
          </p>
          <Link to="/dashboard">
            <Button className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full px-8">
              Ir para o Painel
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1EF]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-[#1C1C1C] hover:text-[#C8A97E]">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Voltar</span>
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="text-lg font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              Lista de Presentes
            </h1>
          </div>
          <Button
            onClick={() => openModal(null, 'casamento')}
            className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full px-6"
            data-testid="add-gift-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Criar Próprio
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="card-dashboard text-center">
            <Gift className="w-8 h-8 text-[#1C1C1C] mx-auto mb-2" />
            <p className="text-2xl font-bold text-[#1C1C1C]">{gifts.length}</p>
            <p className="text-sm text-[#333333]">Total</p>
          </div>
          <div className="card-dashboard text-center">
            <ShoppingBag className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-[#1C1C1C]">{casamentoGifts.length}</p>
            <p className="text-sm text-[#333333]">Casamento</p>
          </div>
          <div className="card-dashboard text-center">
            <Coffee className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-[#1C1C1C]">{chaGifts.length}</p>
            <p className="text-sm text-[#333333]">Lista Complementar</p>
          </div>
          <div className="card-dashboard text-center">
            <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-[#1C1C1C]">{purchasedGifts.length}</p>
            <p className="text-sm text-[#333333]">Presenteados</p>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="grid w-full max-w-lg grid-cols-2 bg-white border border-slate-200">
            <TabsTrigger 
              value="meus_presentes" 
              className="data-[state=active]:bg-[#E8B7B1] data-[state=active]:text-white"
            >
              <Gift className="w-4 h-4 mr-2" />
              Meus Presentes ({gifts.length})
            </TabsTrigger>
            <TabsTrigger 
              value="catalogo" 
              className="data-[state=active]:bg-[#E8B7B1] data-[state=active]:text-white"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Catálogo ({catalog.items.length})
            </TabsTrigger>
          </TabsList>

          {/* Meus Presentes Tab */}
          <TabsContent value="meus_presentes" className="mt-6">
            {gifts.length === 0 ? (
              <div className="card-dashboard text-center py-16">
                <Gift className="w-20 h-20 text-[#333333] mx-auto mb-6" />
                <h3 className="text-xl font-bold text-[#1C1C1C] mb-2">Sua lista está vazia</h3>
                <p className="text-[#333333] mb-6 max-w-md mx-auto">
                  Escolha presentes do nosso catálogo com mais de 120 opções ou crie seus próprios itens personalizados.
                </p>
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={() => setActiveTab('catalogo')}
                    className="bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full px-8"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Ver Catálogo
                  </Button>
                  <Button
                    onClick={() => openModal(null, 'casamento')}
                    variant="outline"
                    className="rounded-full px-8"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Próprio
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Casamento */}
                {casamentoGifts.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-[#1C1C1C] mb-4 flex items-center gap-2">
                      <ShoppingBag className="w-5 h-5 text-[#C8A97E]" />
                      Lista de Casamento ({casamentoGifts.length})
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {casamentoGifts.map((gift) => (
                        <GiftCard key={gift.gift_id} gift={gift} onEdit={openModal} onDelete={handleDelete} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Lista Complementar */}
                {chaGifts.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-[#1C1C1C] mb-4 flex items-center gap-2">
                      <Coffee className="w-5 h-5 text-[#C8A97E]" />
                      Lista Complementar ({chaGifts.length})
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {chaGifts.map((gift) => (
                        <GiftCard key={gift.gift_id} gift={gift} onEdit={openModal} onDelete={handleDelete} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Catálogo Tab */}
          <TabsContent value="catalogo" className="mt-6">
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#333333]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar presente..."
                  className="pl-12 h-12 rounded-full"
                />
              </div>
            </div>

            {/* Category Buttons */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === 'all' 
                    ? 'bg-[#E8B7B1] text-white shadow-sm' 
                    : 'bg-white text-[#333333] border border-slate-200 hover:border-[#E8B7B1] hover:bg-[#FDF7F6]'
                }`}
              >
                Todos ({catalog.items.length})
              </button>
              {Object.entries(catalog.categories).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    selectedCategory === key 
                      ? 'bg-[#E8B7B1] text-white shadow-sm' 
                      : 'bg-white text-[#333333] border border-slate-200 hover:border-[#E8B7B1] hover:bg-[#FDF7F6]'
                  }`}
                >
                  {CATEGORY_ICONS[key]}
                  {label} ({catalog.items.filter(i => i.category === key).length})
                </button>
              ))}
            </div>

                        {/* Catalog Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredCatalog.map((item) => (
                <button
                  key={item.catalog_id}
                  onClick={() => openCatalogModal(item)}
                  className="group h-full rounded-2xl border border-[#E7D5CC] bg-white p-3 sm:p-4 text-left shadow-[0_8px_24px_rgba(28,28,28,0.04)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(28,28,28,0.08)]"
                  data-testid={`catalog-item-${item.catalog_id}`}
                >
                  <div className="relative overflow-hidden rounded-xl mb-3">
                    {item.image_url ? (
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        onError={applyFallbackImage}
                        className="w-full h-36 object-cover rounded-xl transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                    ) : (
                      <div className="w-full h-36 bg-slate-100 rounded-xl flex items-center justify-center">
                        {CATEGORY_ICONS[item.category] || <Gift className="w-12 h-12 text-[#333333]" />}
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
                  </div>
                  <h4 className="font-semibold text-[#1C1C1C] text-[15px] leading-5 line-clamp-2 min-h-[2.5rem]">{item.name}</h4>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <p className="text-[#C8A97E] font-bold text-lg leading-none whitespace-nowrap">{formatCurrency(item.suggested_price)}</p>
                    <span className="inline-flex max-w-[62%] text-xs leading-tight bg-[#F4F1EE] text-[#333333] px-2.5 py-1 rounded-full border border-slate-200">
                      <span className="truncate">{getCatalogCategoryLabel(item.category)}</span>
                    </span>
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#EFE7E2]">
                    <span className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#1C1C1C] bg-[#C8A97E]/12 px-3 py-1.5 rounded-full">
                      <Plus className="w-3 h-3" /> Adicionar à lista
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {filteredCatalog.length === 0 && (
              <div className="text-center py-12">
                <Gift className="w-16 h-16 text-[#333333] mx-auto mb-4" />
                <p className="text-[#333333]">Nenhum item encontrado</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Gift Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              {editingGift ? 'Editar Presente' : 'Criar Presente'}
            </DialogTitle>
            <DialogDescription className="text-[#333333]">
              Crie um presente personalizado para sua lista
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">Nome</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nome do presente"
                className="h-12 rounded-lg"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">Valor (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                className="h-12 rounded-lg"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">Imagem</Label>
              <div className="space-y-2">
                {formData.image_url && (
                  <div className="relative">
                    <img src={formData.image_url} alt="Preview" onError={applyFallbackImage} className="w-full h-32 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1 h-12 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center gap-2 text-[#333333] hover:border-[#E8B7B1] hover:text-[#1C1C1C] transition-colors"
                  >
                    {uploading ? (
                      <span className="animate-spin w-5 h-5 border-2 border-[#E8B7B1] border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Upload do PC
                      </>
                    )}
                  </button>
                </div>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="Ou cole a URL da imagem"
                  className="h-10 rounded-lg text-sm"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_IMAGE_TYPES.join(',')}
                  className="hidden"
                  onChange={handleGiftFileChange}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">Categoria</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full h-12 rounded-lg border border-slate-200 px-4"
              >
                <option value="casamento">Casamento</option>
                <option value="cha_de_panela">Lista Complementar</option>
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-full">
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#E8B7B1] hover:bg-[#DFA8A1] text-white rounded-full">
                {editingGift ? 'Salvar' : 'Adicionar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add from Catalog Modal */}
      <Dialog open={showCatalogModal} onOpenChange={setShowCatalogModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              Adicionar à Lista
            </DialogTitle>
            <DialogDescription className="text-[#333333]">
              Personalize este presente do jeito que preferir
            </DialogDescription>
          </DialogHeader>
          {catalogItem && (
            <div className="space-y-4">
              {/* Preview Image */}
              <div className="relative">
                {(customImage || catalogItem.image_url) ? (
                  <img 
                    src={customImage || catalogItem.image_url} 
                    alt={customName || catalogItem.name}
                    onError={applyFallbackImage}
                    className="w-full h-48 object-cover rounded-xl"
                  />
                ) : (
                  <div className="w-full h-48 bg-slate-100 rounded-xl flex items-center justify-center">
                    {CATEGORY_ICONS[catalogItem.category] || <Gift className="w-16 h-16 text-[#333333]" />}
                  </div>
                )}
                <div className="absolute bottom-3 right-3 flex gap-2">
                  <input
                    ref={catalogFileInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    className="hidden"
                    onChange={handleCatalogFileChange}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => catalogFileInputRef.current?.click()}
                    disabled={uploadingCatalog}
                    className="bg-white/90 hover:bg-white text-[#1C1C1C] rounded-full shadow-lg"
                  >
                    {uploadingCatalog ? (
                      <span className="animate-spin w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <Image className="w-4 h-4 mr-1" />
                        Trocar Foto
                      </>
                    )}
                  </Button>
                  {customImage && customImage !== catalogItem.image_url && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setCustomImage(catalogItem.image_url || '')}
                      className="bg-white/90 hover:bg-white rounded-full shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Custom Name */}
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Nome do Presente
                </Label>
                <Input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Ex: Panelas da Lu"
                  className="h-12 rounded-lg"
                />
                <p className="text-xs text-[#333333] mt-1">
                  Dê um nome mais carinhoso se quiser! Original: {catalogItem.name}
                </p>
              </div>
              
              {/* Custom Price */}
              <div>
                <Label className="text-sm font-bold text-[#1C1C1C] uppercase tracking-wider mb-2 block">
                  Valor Desejado (R$)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  className="h-12 rounded-lg"
                />
                <p className="text-xs text-[#333333] mt-1">
                  Sugestão: {formatCurrency(catalogItem.suggested_price)}
                </p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCatalogModal(false)} className="rounded-full">
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAddFromCatalog}
                  className="bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <SupportWidget />
    </div>
  );
}

// Gift Card Component
function GiftCard({ gift, onEdit, onDelete }) {
  return (
    <div className={`card-dashboard relative ${!gift.is_available ? 'opacity-75' : ''}`}>
      {!gift.is_available && (
        <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10">
          Presenteado
        </div>
      )}
      {gift.image_url ? (
        <img 
          src={gift.image_url} 
          alt={gift.name}
          onError={applyFallbackImage}
          className="w-full h-36 object-cover rounded-lg mb-3"
        />
      ) : (
        <div className="w-full h-36 bg-slate-100 rounded-lg mb-3 flex items-center justify-center">
          <Gift className="w-12 h-12 text-[#333333]" />
        </div>
      )}
      <h4 className="font-bold text-[#1C1C1C] line-clamp-2">{gift.name}</h4>
      <p className="text-lg font-bold text-[#C8A97E] mt-1">{formatCurrency(gift.price)}</p>
      {gift.purchased_by && (
        <p className="text-xs text-[#333333] mt-1">
          Por: {gift.purchased_by}
        </p>
      )}
      {gift.is_available && (
        <div className="flex gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(gift)}
            className="flex-1 rounded-full text-xs"
          >
            <Edit2 className="w-3 h-3 mr-1" />
            Editar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(gift.gift_id)}
            className="text-red-500 hover:bg-red-50 rounded-full"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
}



